import { Component, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { ParameterService } from '@vectronic/parameter';
import { DeviceTypeParameterConfigWithAvailableListAndParameter } from '@vectronic/util';

@Component({
  selector: 'vectronic-parameter-input-config',
  templateUrl: './parameter-input-config.component.html',
  styleUrls: ['./parameter-input-config.component.scss'],
  imports: [MatFormField, MatLabel, MatInput, MatSuffix, MatIcon, MatTooltip, MatIconButton],
})
export class ParameterInputConfigComponent {
  config = input<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  value = input<string>();
  disabled = input(false);
  readonly = input<boolean>();
  resetToFactoryTooltip = input<string>();

  hideLabel = input<boolean>();
  hasClose = input(false);
  showInfo = input(true);
  hasDelete = input(true);
  hasUndo = input(false);
  showReset = input<boolean>();

  configClose = output();
  parameterChange = output<{
    config: DeviceTypeParameterConfigWithAvailableListAndParameter;
    value: string;
  }>();
  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();

  onChange($event: Event, config: DeviceTypeParameterConfigWithAvailableListAndParameter) {
    const { value } = $event.target as HTMLInputElement;
    if (this.factor !== 1) {
      this.parameterChange.emit({ config, value: (parseFloat(value) * this.factor).toFixed(0) });
    } else {
      this.parameterChange.emit({ config, value });
    }
  }

  onClickRemove() {
    this.parameterChange.emit({ config: this.config(), value: undefined });
  }

  getMax() {
    if (!this.config()) return undefined;
    try {
      const max = ParameterService.parseRange(this.config().parameter, this.config())?.max;
      if (max) {
        return parseFloat(max) / this.factor;
      }
      return max;
    } catch {
      return undefined;
    }
  }

  getMin() {
    if (!this.config()) return undefined;
    try {
      const min = ParameterService.parseRange(this.config().parameter, this.config())?.min;
      if (min) {
        return parseFloat(min) / this.factor;
      }
      return min;
    } catch {
      return undefined;
    }
  }

  get factor() {
    if (!this.config()) return undefined;
    if (this.config().parameter.unit === 'Hz') {
      return 1000000;
    }
    return 1;
  }

  get factoredValue() {
    if (!this.config()) return undefined;
    if (this.factor !== 1) {
      return parseFloat(this.value()) / this.factor;
    }
    return this.value();
  }

  get unit() {
    if (!this.config()) return undefined;
    if (this.config().parameter.unit === 'Hz') {
      return 'MHz';
    }
    return this.config().parameter.unit;
  }

  get step() {
    if (!this.config()) return undefined;
    if (this.config().parameter.unit === 'Hz') {
      return 0.001;
    }
    return 1;
  }

  get parameterTitle() {
    const { name, subcategory } = this.config().parameter;
    if (subcategory) return `${subcategory} / ${name}`;
    else return name;
  }

  onClickClose() {
    this.configClose.emit();
  }
  onClickUndo() {
    this.parameterUndo.emit(this.config());
  }

  onResetToFactory() {
    this.resetToFactory.emit(this.config());
  }
}
